// The candidate profile (`#candidates/<token>[/<tab>]`, DS DC-78…DC-81, DC-88 / candidate C-1…C-4, C-9, C-11, C-15):
// "One candidate master profile may be linked to multiple requisition-specific applications over time." The hero
// carries the Candidate ID and the profile pills; the tabs are his surfaces (Overview · Applications · Resume versions · Candidate timeline · Talent
// community · Internal mobility). Every value is what candidate.profile lets this seat see: in full for the owner, the
// seated and TA leadership, the Candidate ID alone for the HR Business Partner, the membership and loop facts for a
// rediscovered talent-community member (opened by the caller's own search ref, `#candidates/ref/<ref>`). An application
// the reader is not seated on shows its requisition's loop facts only; a timeline line of such a requisition is absent.
// A merged profile says so and offers Recover to the Admin. Unknown or unauthorised → not found.
// Every dialog opens from the URL (`?action=edit|link|designate|merge|recover`).
// UAT D-8 (his design): the hero reads "<title> at <employer>"; a 320 px rail beside the tabs holds the Application card
// of the application the profile is read for now (Sourced · Source · Requisition · Stage with "In stage n days · Active
// Time" · Owner — comp expectation and availability are §6 fields the profile read does not carry); Overview carries
// its stage track; on Applications, Stage is the DC-31 column, Status his three words and the newest line follows; a
// timeline line is the server's sentence with its channel and its ids as pills; "Original resume" is v1 only, the file
// name is the link and the eye opens its docpane with the confirmed values. No standing banner (C-8), and no requirement
// sentence drawn as screen copy (UAT F-32, Q-4's default: it stays in the documents). The person's timeline is read
// once, where the profile shows it, for all of these.
// Hosted by MissionShell: views/b3/register.ts registers it through S12's registerView, so it draws no McRoot of its own.

import { useEffect, useState } from "react";
import type { CandidateProfileOutput, ProfileApplication, StageClock } from "@/api/tools";
import { candidateParseOpen, candidateProfile, candidateTimeline } from "@/api/tools";
import { isClientRefusal } from "@/api/useTool";
import { go, useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { NotFound } from "@/views/NotFound";
import {
  ACTION_LABELS, APPLICATION_STATUS, Banner, Button, Card, CandidateHero, COMMON_LABELS, DEFAULT_TIME_ZONE, DIALOG_LABELS, DocPane, DOCUMENT_LABELS,
  EmptyState, ErrorStrip, Flex, formatDate, G1_LABELS, G6_TEMPLATES, G7_TEMPLATES, HERO_LABELS, History, HISTORY_LABELS, IconButton, IdPill,
  KeyValue, Lines, MenuButton, NAV_ITEM_LABELS, Pill, Rail, RULE_LABELS, Skeleton, Split, Stack, Table, Tabs, Td, TextLink, Th, Tr,
  type KeyValueItem,
} from "@/mc";
import { BRD } from "./brd";
import { useSourceWord } from "./options";
import { queryOf, useSettledRead, withQuery } from "@/views/shared/settledRead";
import { CANDIDATE_LABELS as L, PROFILE_LABELS as P } from "./labels";
import {
  applicationStatus, clockOf, currentApplication, heroHeadline, parsesOf, profileHref, reasonWord, requisitionWord, resumeWord, stageTrack,
  stageWord, STATUS_WORD, tabOf, timelineEvents, type ProfileTab,
} from "./profile";
import { ProfileDialogs } from "./ProfileDialogs";
import { InternalMobilityTab, TalentCommunityTab } from "./ProfileTabs";
import { profileTimelineBody } from "./slots";

export interface ProfileSubject {
  /** The opaque token (the route key); null for a rediscovered member named by the caller's ref. */
  token: string | null;
  ref: string | null;
}

export function ProfilePage({ subject, tab }: { subject: ProfileSubject; tab: string | undefined }) {
  return <ProfileBody subject={subject} tab={tab} />;
}

export function useProfile(subject: ProfileSubject) {
  return useSettledRead(["candidate-profile", subject.token, subject.ref], () =>
    candidateProfile(subject.token ? { candidate_id: subject.token } : { candidate_id: null, ref: subject.ref }));
}

/** The person's timeline, read once for the rail, Applications, the résumé docpanes and the timeline tab — only where
 *  the profile shows it: the seat reads timelines and holds more than the Candidate ID. */
function usePersonTimeline(profile: CandidateProfileOutput | undefined) {
  const { can } = useSession();
  const id = profile?.candidate_id ?? "";
  return useSettledRead(["candidate-timeline", id], () => candidateTimeline({ candidate_id: id, limit: 200 }), {
    enabled: Boolean(id) && profile?.projection !== "token" && can("candidate.timeline"),
  });
}

const useTimeZone = (): string => useSession().me?.timezone || DEFAULT_TIME_ZONE;

function ProfileBody({ subject, tab: tabPart }: { subject: ProfileSubject; tab: string | undefined }) {
  const route = useRoute();
  const { register } = useViewState();
  const flags = queryOf(route.raw);
  const tab = tabOf(tabPart);
  const profile = useProfile(subject);
  const data = profile.data;
  const timeline = usePersonTimeline(data);
  const timeZone = useTimeZone();
  useEffect(
    () => register({ view: "candidates", ids: { candidate: subject.token ?? "" }, summary: { tab: tab ?? "", status: data?.status ?? "loading" } }),
    [register, subject.token, tab, data?.status],
  );

  if (tab === null) return <NotFound route={route.raw} />;
  if (profile.error && isClientRefusal(profile.error)) return <NotFound route={route.raw} />;
  if (profile.error) {
    return (
      <ErrorStrip
        message={RULE_LABELS.failedSyncRetryable}
        problem={{ status: profile.error.status, refusal_code: profile.error.code, request_id: profile.error.requestId }}
        onRetry={() => void profile.refetch()}
        
      />
    );
  }
  if (!data) {
    return (
      <Stack gap={16}>
        <Skeleton height={120} />
        <Skeleton lines={6} height={18} />
      </Stack>
    );
  }
  const clocks = timeline.data?.applications;
  const tabs = <ProfileTabs profile={data} subject={subject} tab={tab} clocks={clocks} timeline={timeline} timeZone={timeZone} />;
  return (
    <Stack gap={16}>
      <Flex gap={8}><TextLink href="#candidates" >{NAV_ITEM_LABELS.candidates}</TextLink></Flex>
      <Hero profile={data} subject={subject} flags={flags} />
      {currentApplication(data) ? <Split main={tabs} side={<ApplicationCard profile={data} clocks={clocks} timeZone={timeZone} />} /> : tabs}
      <ProfileDialogs profile={data} subject={subject} flags={flags} onDone={() => void profile.refetch()} />
    </Stack>
  );
}

type PersonTimeline = ReturnType<typeof usePersonTimeline>;

function Hero({ profile, subject, flags }: { profile: CandidateProfileOutput; subject: ProfileSubject; flags: URLSearchParams }) {
  const { can } = useSession();
  const full = profile.projection === "full";
  const active = profile.status !== "Merged";
  const internal = (profile.internal ?? []).length > 0 || profile.source_channel === "internal";
  const here = profileHref(subject);
  const open = (action: string, extra: Record<string, string | null> = {}) => go(withQuery(here, { action, ...extra }, flags));
  const menu = [
    { label: ACTION_LABELS.merge, onSelect: () => open("merge"), can: can("candidate.merge") && full && active, hs: "cand.profile.merge" },
    { label: ACTION_LABELS.designate, onSelect: () => open("designate"), can: can("candidate.community_add") && full && active, hs: "cand.profile.designate" },
  ].filter((item) => item.can);
  // The icon-only menu is named by what it holds: a seat without Merge never hears "Merge".
  const facts = full ? [
    profile.email ? { icon: "mail" as const, text: profile.email } : null,
    profile.phone ? { icon: "phone" as const, text: profile.phone } : null,
    profile.linkedin_url ? { icon: "link" as const, text: profile.linkedin_url } : null,
    profile.location ? { icon: "pin" as const, text: profile.location } : null,
    profile.current_employer ? { icon: "brief" as const, text: profile.current_employer } : null,
  ].filter((fact) => fact !== null) : [];
  return (
    <CandidateHero
      name={profile.display_name ?? profile.candidate_no}
      candidateId={profile.candidate_no}
      level={1}
      
      pills={(
        <>
          <Pill tone={profile.status === "Merged" ? "violet" : profile.status === "Active" ? "info" : "neutral"}>{STATUS_WORD[profile.status]}</Pill>
          {full ? (internal ? <Pill tone="violet">{HERO_LABELS.internal}</Pill> : <Pill tone="neutral">{HERO_LABELS.external}</Pill>) : null}
          {profile.community ? <Pill tone="info">{HERO_LABELS.talentCommunity}</Pill> : null}
        </>
      )}
      headline={full ? heroHeadline(profile) : undefined}
      facts={facts}
      actions={(
        <Flex gap={8}>
          <Button tone="ghost" can={can("candidate.update_profile") && full && active} onClick={() => open("edit")} >
            {DIALOG_LABELS.editProfile}
          </Button>
          <Button tone="blue" can={can("candidate.link_to_requisition") && active && profile.projection !== "token"} onClick={() => open("link")} >
            {DIALOG_LABELS.linkToRequisition}
          </Button>
          {menu.length > 0 ? <MenuButton label={menu.map((item) => item.label).join(" · ")} icon="dots" iconOnly items={menu} align="end"  /> : null}
        </Flex>
      )}
      foot={profile.status === "Merged" ? <MergedBanner profile={profile} onRecover={() => open("recover")} /> : undefined}
    />
  );
}

/** The rail's Application card (CA-8): the application the profile is read for now — the date it was Sourced, Source,
 *  Requisition, Stage with its aging line "In stage n days · Active Time" (DC-97, the board's own number) and Owner. */
export function ApplicationCard({ profile, clocks, timeZone }: { profile: CandidateProfileOutput; clocks: readonly StageClock[] | undefined; timeZone: string }) {
  const sourceWord = useSourceWord();
  const app = currentApplication(profile);
  if (!app?.application_id) return null;
  const clock = clockOf(clocks, app.application_id);
  const stage = stageWord({ stage: clock?.stage ?? app.stage, held_from_stage: clock?.held_from_stage });
  const days = clock?.in_stage_days;
  const items: KeyValueItem[] = [
    [APPLICATION_STATUS.sourced, clock ? formatDate(clock.sourced_at, timeZone) : "—"],
    [L.source, dash(sourceWord(profile.source_channel))],
    [BRD.requisition, <Requisition key="req" app={app}  />],
    [BRD.stage, (
      <Stack gap={2} key="stage">
        <span>{dash(stage)}</span>
        {days !== null && days !== undefined ? <span>{G6_TEMPLATES.inStage({ n: days })}</span> : null}
      </Stack>
    )],
    [L.owner, dash(app.owner_name)],
  ];
  return <Card title={P.application}><KeyValue items={items} cols={1} /></Card>;
}

function Requisition({ app, hs }: { app: ProfileApplication; hs: string }) {
  const text = `${app.requisition_id} · ${app.title}`;
  return app.application_id ? <TextLink href={`#requisitions/${app.requisition_id}/candidates/${app.application_id}`} hs={hs}>{text}</TextLink> : <>{text}</>;
}

function MergedBanner({ profile, onRecover }: { profile: CandidateProfileOutput; onRecover: () => void }) {
  const { can } = useSession();
  const mayRecover = can("candidate.unmerge") && Boolean(profile.recovery);
  return (
    <Banner
      tone="warn"
      right={mayRecover ? <Button tone="ghost" sm onClick={onRecover} >{ACTION_LABELS.recover}</Button> : undefined}
    >
      <Flex gap={8} align="center" wrap>
        <span>{STATUS_WORD.Merged}</span>
        {profile.merged_into_no ? <IdPill value={profile.merged_into_no}  /> : null}
      </Flex>
    </Banner>
  );
}

function ProfileTabs({ profile, subject, tab, clocks, timeline, timeZone }: {
  profile: CandidateProfileOutput; subject: ProfileSubject; tab: ProfileTab; clocks: readonly StageClock[] | undefined; timeline: PersonTimeline; timeZone: string;
}) {
  const internal = (profile.internal ?? []).length > 0;
  const items: { key: ProfileTab; label: string; count?: number }[] = [
    { key: "overview", label: P.overview },
    { key: "applications", label: L.applications, count: (profile.applications ?? []).length },
    ...(profile.projection === "full" ? [{ key: "resumes" as const, label: DOCUMENT_LABELS.resumeVersions, count: (profile.resume_versions ?? []).length }] : []),
    ...(profile.candidate_id && profile.projection !== "token" ? [{ key: "timeline" as const, label: HISTORY_LABELS.candidateTimeline }] : []),
    { key: "talent-community", label: NAV_ITEM_LABELS.talentCommunity },
    ...(internal ? [{ key: "internal-mobility" as const, label: P.internalMobility }] : []),
  ];
  const shown = items.some((item) => item.key === tab) ? tab : "overview";
  return (
    <Stack gap={12}>
      <Tabs
        label={profile.display_name ?? profile.candidate_no}
        route
        active={shown}
        items={items.map((item) => ({ ...item, href: profileHref(subject, item.key), hs: `cand.profile.tab.${item.key}` }))}
      />
      {shown === "overview" ? <Overview profile={profile} clocks={clocks} /> : null}
      {shown === "applications" ? <ApplicationsTable profile={profile} clocks={clocks} timeZone={timeZone} /> : null}
      {shown === "resumes" ? <Resumes profile={profile} parses={parsesOf(timeline.data?.rows ?? [])} /> : null}
      {shown === "timeline" && profile.candidate_id ? <Timeline candidateId={profile.candidate_id} timeline={timeline} timeZone={timeZone} /> : null}
      {shown === "talent-community" ? <TalentCommunityTab profile={profile} subject={subject} /> : null}
      {shown === "internal-mobility" ? <InternalMobilityTab profile={profile} /> : null}
    </Stack>
  );
}

const dash = (value: string | null | undefined): string => (value ? value : "—");

function Overview({ profile, clocks }: { profile: CandidateProfileOutput; clocks: readonly StageClock[] | undefined }) {
  const sourceWord = useSourceWord();
  const app = currentApplication(profile);
  const clock = clockOf(clocks, app?.application_id);
  const track = app ? stageTrack({ stage: clock?.stage ?? app.stage, held_from_stage: clock?.held_from_stage }) : null;
  const items: KeyValueItem[] = [[COMMON_LABELS.candidateId, <span className="mc-mono" key="id">{profile.candidate_no}</span>, undefined, 2]];
  if (profile.projection === "full") {
    items.push(
      [L.name, dash(profile.display_name)],
      [L.primaryEmail, dash(profile.email)],
      [L.secondaryEmail, dash((profile.secondary_emails ?? []).join(", "))],
      [L.phone, dash(profile.phone)],
      [L.linkedinUrl, dash(profile.linkedin_url)],
      [L.location, dash(profile.location)],
      [BRD.currentEmployer, dash(profile.current_employer)],
      [L.source, dash(sourceWord(profile.source_channel))],
      [L.owner, dash(profile.owner_name)],
    );
    if (profile.no_email_exception) items.push([L.noEmailException, profile.no_email_exception.reason]);
    const history = profile.employment_history ?? [];
    if (history.length > 0) {
      items.push([BRD.employmentHistory, (
        <Stack gap={4} key="history">
          {history.map((row, index) => (
            <span key={index}>{[row.employer, row.title, [row.start, row.end].filter(Boolean).join(" – ")].filter(Boolean).join(" · ")}</span>
          ))}
        </Stack>
      ), undefined, 3]);
    }
  } else if (profile.display_name) {
    items.push([L.name, profile.display_name]);
  }
  items.push([L.status, STATUS_WORD[profile.status]]);
  return (
    <Stack gap={12}>
      {app && track ? (
        <Card title={`${app.requisition_id} · ${app.title}`}>
          <Rail
            label={`${BRD.stage} · ${app.requisition_id}`}
            steps={track}
            wrap
            meta={clock?.in_stage_days !== null && clock?.in_stage_days !== undefined ? G6_TEMPLATES.inStage({ n: clock.in_stage_days }) : undefined}
          />
        </Card>
      ) : null}
      <Card><KeyValue items={items} cols={3} /></Card>
    </Stack>
  );
}

/** Applications (DC-81, CA-4): each application keeps its own stage — the DC-31 column — and status, then its newest
 *  line; an application the reader is not seated on shows its requisition's state alone. The Talent community column
 *  draws a designation only when the person holds a membership row, so it agrees with the hero and the Talent community
 *  tab ("talent-community eligibility and membership stored separately"; UAT F-34). */
export function ApplicationsTable({ profile, clocks, timeZone }: { profile: CandidateProfileOutput; clocks: readonly StageClock[] | undefined; timeZone: string }) {
  const rows = profile.applications ?? [];
  return (
    <Table
      dense
      label={L.applications}
      cols={6}
      empty={rows.length === 0 ? "—" : undefined}
      head={(
        <Tr>
          <Th>{BRD.requisition}</Th>
          <Th>{L.status}</Th>
          <Th>{BRD.stage}</Th>
          <Th>{HISTORY_LABELS.candidateTimeline}</Th>
          <Th>{L.owner}</Th>
          <Th>{L.talentCommunity}</Th>
        </Tr>
      )}
    >
      {rows.map((app, index) => {
        const seated = Boolean(app.application_id);
        const clock = clockOf(clocks, app.application_id);
        const eligible = profile.community ? (profile.eligibility ?? []).find((e) => e.application_id === app.application_id) : undefined;
        return (
          <Tr key={`${app.requisition_id}-${app.application_id ?? index}`} >
            <Td label={BRD.requisition}>
              {seated && profile.candidate_id ? <Requisition app={app}  /> : `${app.requisition_id} · ${app.title}`}
            </Td>
            <Td label={L.status}>{seated ? dash(applicationStatus(app.stage)) : requisitionWord(app.state)}</Td>
            <Td label={BRD.stage}>{seated ? dash(stageWord({ stage: clock?.stage ?? app.stage, held_from_stage: clock?.held_from_stage })) : "—"}</Td>
            <Td label={HISTORY_LABELS.candidateTimeline} sub={clock?.last_at ? formatDate(clock.last_at, timeZone) : undefined}>{dash(clock?.last_sentence)}</Td>
            <Td label={L.owner}>{dash(app.owner_name)}</Td>
            <Td label={L.talentCommunity}>{eligible ? <Pill tone="info">{reasonWord(eligible.basis)}</Pill> : "—"}</Td>
          </Tr>
        );
      })}
    </Table>
  );
}

function Resumes({ profile, parses }: { profile: CandidateProfileOutput; parses: ReadonlyMap<number, string> }) {
  const [open, setOpen] = useState<number | null>(null);
  return <ResumesTable profile={profile} parses={parses} open={open} onOpen={(version) => setOpen((now) => (now === version ? null : version))} />;
}

/** Resume versions (CA-17): "Original resume" on the first version only, the file name is the link to its file, and
 *  the eye opens the version's docpane with the values confirmed from it ("Store the original resume and
 *  parsed/confirmed values."), where the seat may open the parse. The upload day reads in the app's format (UAT F-73). */
export function ResumesTable({ profile, parses, open, onOpen }: {
  profile: CandidateProfileOutput; parses: ReadonlyMap<number, string>; open: number | null; onOpen: (version: number) => void;
}) {
  const { can } = useSession();
  const timeZone = useTimeZone();
  const rows = profile.resume_versions ?? [];
  if (rows.length === 0) return <EmptyState icon="file">{DOCUMENT_LABELS.resumeVersions}</EmptyState>;
  const opened = open === null ? undefined : rows.find((version) => version.version === open);
  const parse = open === null ? undefined : parses.get(open);
  return (
    <Stack gap={12}>
      <Table
        dense
        label={DOCUMENT_LABELS.resumeVersions}
        cols={5}
        head={(
          <Tr>
            <Th>{DOCUMENT_LABELS.resumeVersions}</Th>
            <Th>{COMMON_LABELS.candidateId}</Th>
            <Th>{HISTORY_LABELS.actor}</Th>
            <Th>{HISTORY_LABELS.timestamp}</Th>
            <Th>{L.status}</Th>
          </Tr>
        )}
      >
        {rows.map((version, index) => (
          <Tr key={version.file_id} >
            <Td label={DOCUMENT_LABELS.resumeVersions}>
              <Flex gap={6} align="center">
                <span className="mc-mono">{G7_TEMPLATES.version({ n: version.version })}</span>
                <TextLink href={`/api/files/${version.file_id}`} >{version.original_name || resumeWord(version.version)}</TextLink>
                <IconButton
                  icon="eye"
                  sm
                  label={resumeWord(version.version)}
                  can={can("candidate.parse_open") && parses.has(version.version)}
                  aria-pressed={open === version.version}
                  onClick={() => onOpen(version.version)}
                  
                />
              </Flex>
            </Td>
            <Td label={COMMON_LABELS.candidateId}><span className="mc-mono">{version.candidate_no}</span></Td>
            <Td label={HISTORY_LABELS.actor}>{dash(version.uploaded_by_name)}</Td>
            <Td label={HISTORY_LABELS.timestamp}>{version.uploaded_at ? formatDate(version.uploaded_at, timeZone) : "—"}</Td>
            <Td label={L.status}>
              <Flex gap={4} wrap>
                {version.version === 1 ? <Pill tone="neutral">{DOCUMENT_LABELS.originalResume}</Pill> : null}
                {/* "Store the original resume and parsed/confirmed values.": the parse's values as FieldState names them */}
                {version.confirmed ? <Pill tone="ok">{G1_LABELS.confirmed}</Pill> : version.parsed ? <Pill tone="info">{G1_LABELS.proposed}</Pill> : null}
              </Flex>
            </Td>
          </Tr>
        ))}
      </Table>
      {opened && parse && can("candidate.parse_open") ? <ResumePane parseId={parse} version={opened.version} /> : null}
    </Stack>
  );
}

/** The confirmed values beside the version's pages, in the order the review asked them (Review parsed values). */
const CONFIRMED_FIELDS: readonly { key: string; label: string }[] = [
  { key: "name", label: L.name },
  { key: "email", label: L.email },
  { key: "phone", label: L.phone },
  { key: "location", label: L.location },
  { key: "current_employer", label: BRD.currentEmployer },
  { key: "linkedin_url", label: L.linkedinUrl },
];

function ResumePane({ parseId, version }: { parseId: string; version: number }) {
  const read = useSettledRead(["candidate-parse", parseId], () => candidateParseOpen({ parse_id: parseId }));
  const [page, setPage] = useState(1);
  if (read.error) {
    return <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: read.error.status, refusal_code: read.error.code, request_id: read.error.requestId }} onRetry={() => void read.refetch()} />;
  }
  if (!read.data) return <DocPane loading title={resumeWord(version)} fields={<Skeleton lines={6} height={18} />} />;
  const pages = (read.data.text ?? "").split("\f");
  const confirmed = read.data.confirmed ?? {};
  const items: KeyValueItem[] = CONFIRMED_FIELDS.map(({ key, label }) => [label, typeof confirmed[key] === "string" && confirmed[key] ? String(confirmed[key]) : "—"]);
  return (
    <DocPane
      title={resumeWord(version)}
      sub={read.data.status === "confirmed" ? <Pill tone="ok">{G1_LABELS.confirmed}</Pill> : <Pill tone="info">{G1_LABELS.proposed}</Pill>}
      pages={pages.length}
      page={page}
      onPage={setPage}
      fields={<KeyValue items={items} cols={1} />}
      
    >
      {(n: number) => <Lines plain sm items={(pages[n - 1] ?? "").split("\n")} />}
    </DocPane>
  );
}

/** The Candidate timeline tab: a body another lane registers (slots.ts, C-B10-13; B10 S18's spine timeline), else ours. */
function Timeline({ candidateId, timeline, timeZone }: { candidateId: string; timeline: PersonTimeline; timeZone: string }) {
  const Body = profileTimelineBody.list()[0]?.component;
  return Body ? <Body candidateId={candidateId} /> : <CandidateHistory read={timeline} timeZone={timeZone} />;
}

function CandidateHistory({ read, timeZone }: { read: PersonTimeline; timeZone: string }) {
  return (
    <Card title={HISTORY_LABELS.candidateTimeline}>
      {read.error ? (
        <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: read.error.status, refusal_code: read.error.code, request_id: read.error.requestId }} onRetry={() => void read.refetch()} />
      ) : (
        <History label={HISTORY_LABELS.candidateTimeline} events={timelineEvents(read.data?.rows ?? [])} timeZone={timeZone} loading={read.isLoading ? 5 : undefined} max={200} />
      )}
    </Card>
  );
}
